type DisclaimerType = "brightcove" | "libsyn";
type Deferred<T> = { promise: Promise<T>; resolve: (value: T) => void; reject: (reason?: unknown) => void };
import { moduletracking } from "../Wyanoke/ModuleTracking";

/** Plain disclaimer text. */
export type DisclaimerMessage = string | { beforeItSupportEmail: string };

const DEFAULT_BRIGHTCOVE_DISCLAIMER_BEFORE_CONTACT =
  "Closed captioning in progress — for immediate access, email itsupport@wyanokegroup.com";
const DEFAULT_LIBSYN_DISCLAIMER_BEFORE_CONTACT =
  "Transcript in progress — for immediate access, email itsupport@wyanokegroup.com";

const DEFAULT_CONTAINER_SELECTORS = [
  "main",
  "[role='main']",
  ".container",
  "body"
];

function deferred<T>(): Deferred<T> {
  let resolve: (value: T) => void = () => undefined;
  let reject: (reason?: unknown) => void = () => undefined;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function firstContainer(): HTMLElement {
  for (const selector of DEFAULT_CONTAINER_SELECTORS) {
    const el = document.querySelector(selector);
    if (el) {
      return el as HTMLElement;
    }
  }
  return document.body;
}

function insertAfter(newNode: HTMLElement, afterNode: Element): boolean {
  const parent = afterNode.parentNode;
  if (!parent) {
    return false;
  }
  if (afterNode.nextSibling) {
    parent.insertBefore(newNode, afterNode.nextSibling);
  } else {
    parent.appendChild(newNode);
  }
  return true;
}

function normalizeBrightcoveAnchorBottomSpacing(anchor: Element): void {
  const tag = anchor.tagName.toLowerCase();
  if (tag !== "div" && tag !== "p") {
    return;
  }
  // Brightcove can render either as a <video-js> custom element or a classic
  // <div class="video-js"> wrapper. Treat either as a player container.
  const hasBrightcovePlayerMarkup =
    Boolean(anchor.querySelector("video-js")) ||
    Boolean(anchor.querySelector(".video-js")) ||
    Boolean(anchor.querySelector("video[data-account], video[data-video-id]"));
  if (!hasBrightcovePlayerMarkup) {
    return;
  }
  (anchor as HTMLElement).style.setProperty("margin-bottom", "0", "important");
}

// Podcast Series specific styling
function applyMostRecentEpisodePlayerDisclaimerSpacing(el: HTMLElement, anchor?: Element | null): void {
  const target = anchor || null;
  if (!target) {
    return;
  }

  const tag = target.tagName.toLowerCase();
  const isMediaElement = tag === "iframe" || tag === "video";
  if (!isMediaElement) {
    return;
  }

  if (target.closest(".most-recent-episode-player")) {
    el.style.setProperty("margin-bottom", "16px", "important");
  }
}

function applyModuleTrackingAttributes(el: HTMLElement, type: DisclaimerType): void {
  el.setAttribute("data-module-track-category", "Media Accessibility Disclaimer");
  el.setAttribute("data-module-track-action", "View");
  el.setAttribute("data-module-track-label", `Media Accessibility Disclaimer - ${type}`);
}

function trackDisclaimerViewOnce(el: HTMLElement, type: DisclaimerType): void {
  if (!el.isConnected) {
    return;
  }
  if (el.getAttribute("data-disclaimer-view-tracked") === "1") {
    return;
  }
  try {
    moduletracking.UseGoogleTrack(
      "Media Accessibility Disclaimer",
      "View",
      `Media Accessibility Disclaimer - ${type}`
    );
    el.setAttribute("data-disclaimer-view-tracked", "1");
  } catch {
    // Ignore tracking failures so disclaimer rendering still succeeds.
  }
}

function rebindModuleTrackingForDisclaimer(el: HTMLElement): void {
  try {
    moduletracking.AjaxTrackLinks(el, "MediaAccessibilityDisclaimer");
  } catch {
    // Ignore tracking bind failures so disclaimer rendering still succeeds.
  }
}

function setDisclaimerContent(el: HTMLElement, message: DisclaimerMessage): void {
  if (typeof message === "string") {
    el.textContent = message;
    return;
  }
  el.textContent = `${message.beforeItSupportEmail}itsupport@wyanokegroup.com`;
}

function ensureDisclaimer(
  type: DisclaimerType,
  message: DisclaimerMessage,
  anchor?: Element | null,
  idSuffix?: string
): HTMLElement {
  const id = idSuffix
    ? `accessibility-disclaimer-${type}-${idSuffix}`
    : `accessibility-disclaimer-${type}`;
  const existing = document.getElementById(id);
  if (existing) {
    applyModuleTrackingAttributes(existing as HTMLElement, type);
    existing.className = "accessibility-disclaimer";
    (existing as HTMLElement).style.cssText = [
      "font-size:12px",
      "line-height:1.45",
      "letter-spacing:-0.1px",
      "color:rgb(85, 95, 97)",
      "background:transparent",
    // "margin:0",
    // "padding:8px 10px",
      "margin:0 0 32px",
      "padding:8px 0",
      "display:block",
      "width:100%",
      "box-sizing:border-box",
      "font-family:\"Roboto Condensed\", sans-serif"
    ].join(";");
    if (type === "libsyn") {
      (existing as HTMLElement).style.marginBottom = "32px";
    }
    applyMostRecentEpisodePlayerDisclaimerSpacing(existing as HTMLElement, anchor);
    setDisclaimerContent(existing as HTMLElement, message);
    // Brightcove View fires only after catalog metadata confirms missing captions.
    rebindModuleTrackingForDisclaimer(existing as HTMLElement);
    return existing;
  }

  const el = document.createElement("div");
  el.id = id;
  el.className = "accessibility-disclaimer";
  el.setAttribute("role", "note");
  applyModuleTrackingAttributes(el, type);
  setDisclaimerContent(el, message);
  el.style.cssText = [
    "font-size:12px",
    "line-height:1.45",
    "letter-spacing:-0.1px",
    "color:rgb(85, 95, 97)",
    "background:transparent",
    // "margin:0",
    // "padding:8px 10px",
    "margin:0 0 32px",
    "padding:8px 0",
    "display:block",
    "width:100%",
    "box-sizing:border-box",
    "font-family:\"Roboto Condensed\", sans-serif"
  ].join(";");
  if (type === "libsyn") {
    el.style.marginBottom = "32px";
  }
  applyMostRecentEpisodePlayerDisclaimerSpacing(el, anchor);

  // If an anchor was provided, prefer inserting after it — but fall back gracefully
  // if that element has been removed/replaced before we insert (common with async checks).
  if (anchor) {
    if (type === "brightcove") {
      normalizeBrightcoveAnchorBottomSpacing(anchor);
    }

    // Libsyn embeds are commonly inline iframes inside <p>; forcing block layout removes
    // the baseline-descender gap that appears between iframe and following content.
    if (type === "libsyn" && anchor.tagName.toLowerCase() === "iframe") {
      const iframe = anchor as HTMLIFrameElement;
      iframe.style.display = "block";
      iframe.style.verticalAlign = "bottom";
      iframe.style.margin = "0";
    }

    const anchorAny = anchor as any;
    const isConnected = typeof anchorAny?.isConnected === "boolean" ? (anchorAny.isConnected as boolean) : Boolean(anchor.parentNode);
    if (isConnected && insertAfter(el, anchor)) {
      // Brightcove View fires only after catalog metadata confirms missing captions.
      rebindModuleTrackingForDisclaimer(el);
      return el;
    }
  }

  const container = firstContainer();
  container.insertBefore(el, container.firstChild);
  rebindModuleTrackingForDisclaimer(el);
  return el;
}

function removeDisclaimer(type: DisclaimerType, idSuffix?: string): void {
  const id = idSuffix
    ? `accessibility-disclaimer-${type}-${idSuffix}`
    : `accessibility-disclaimer-${type}`;
  const el = document.getElementById(id);
  if (el?.parentNode) {
    el.parentNode.removeChild(el);
  }
}

function normalizeDisclosureKeyPart(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
}

function getBrightcoveDisclaimerKey(embed: Element, index: number): string {
  const mediaSrc = (embed.getAttribute("src") || "").trim();
  const candidates = [
    (embed as HTMLElement).id || "",
    embed.getAttribute("data-video-id") || "",
    embed.getAttribute("data-account") || "",
    mediaSrc
  ];

  for (const raw of candidates) {
    const key = normalizeDisclosureKeyPart(raw);
    if (key) {
      return key;
    }
  }

  return `embed-${index + 1}`;
}

function removeStaleBrightcoveDisclaimers(validKeys: Set<string>): void {
  const nodes = Array.from(document.querySelectorAll("[id^='accessibility-disclaimer-brightcove']"));
  for (const node of nodes) {
    const id = node.id || "";
    if (id === "accessibility-disclaimer-brightcove") {
      // Remove the prior single-instance Brightcove disclaimer once we are in
      // multi-instance mode.
      removeDisclaimer("brightcove");
      continue;
    }
    const key = id.replace(/^accessibility-disclaimer-brightcove-/, "");
    if (!validKeys.has(key)) {
      removeDisclaimer("brightcove", key);
    }
  }
}

function hasAnyTranscriptElement(): boolean {
  const candidates = [
    "[data-transcript]",
    "[data-has-transcript='true']",
    "a.transcript",
    "a[class~='transcript']",
    ".transcript",
    ".transcript-header",
    ".transcript-content",
    ".transcript-body",
    ".transcript-footer",
    "#transcript",
    "[id*='transcript']",
    "[class*='transcript']"
  ];
  return candidates.some(sel => Boolean(document.querySelector(sel)));
}

function pageHasLibsynEmbed(): boolean {
  return Array
    .from(document.querySelectorAll("iframe"))
    .some(iframe => (iframe.getAttribute("src") || "").toLowerCase().includes("libsyn.com"));
}

function findLibsynAnchor(): Element | null {
  // Prefer the actual playable element so disclaimer sits right under the player.
  const iframe = Array
    .from(document.querySelectorAll("iframe"))
    .find(i => (i.getAttribute("src") || "").toLowerCase().includes("libsyn.com"));
  if (iframe) {
    return iframe;
  }

  // For DOM embeds, prefer the inner player region over the outer grid wrapper.
  const grid = document.querySelector(".player-grid");
  if (grid) {
    const innerPlayer =
      grid.querySelector("iframe[src*='libsyn.com']") ||
      grid.querySelector("audio, video") ||
      grid.querySelector("[class*='player-grid__player'], [class*='player-grid__body']");
    return innerPlayer || grid;
  }

  // Next: a prominent Libsyn download link.
  const download = document.querySelector("a[href*='traffic.libsyn.com']");
  if (download) {
    return download;
  }

  // Fallback: any Libsyn-ish node.
  return document.querySelector("[src*='libsyn.com'], a[href*='libsyn.com'], [style*='libsyn.com']");
}

function findBrightcoveEmbeds(): Element[] {
  const selectors = [
    // Brightcove iframe embed
    "iframe[src*='brightcove']",
    "iframe[src*='players.brightcove.net']",
    // Brightcove video-js embed (Video.js)
    "video-js[data-account][data-player]",
    "video-js[data-video-id]",
    "video[data-account]",
    "video[data-video-id]"
  ];

  const set = new Set<Element>();
  selectors.forEach(sel => {
    document.querySelectorAll(sel).forEach(el => {
      // Normalize nested Brightcove tech nodes to one logical embed so a single
      // player does not produce duplicate disclaimers.
      if (el.tagName.toLowerCase() === "video") {
        const nestedVideoJs = el.closest("video-js");
        if (nestedVideoJs) {
          set.add(nestedVideoJs);
          return;
        }
      }
      set.add(el);
    });
  });
  return Array.from(set);
}

function videoElementHasCaptionOrSubtitleTracks(video: Element): boolean {
  return Array.from(video.querySelectorAll("track")).some(t => {
    const kind = (t.getAttribute("kind") || "").toLowerCase();
    return kind === "captions" || kind === "subtitles";
  });
}

/**
 * Declarative caption/subtitle tracks on the HTML5 `<video>` (plain embed or Video.js tech).
 * Does not rely on Video.js `textTracks()` or `.vjs-subs-caps-button`.
 */
function brightcoveEmbedHasCaptionTracksInMarkup(embed: Element): boolean {
  const tag = embed.tagName.toLowerCase();
  if (tag === "video") {
    return videoElementHasCaptionOrSubtitleTracks(embed);
  }
  if (tag === "video-js") {
    const tech = embed.querySelector("video");
    return tech ? videoElementHasCaptionOrSubtitleTracks(tech) : false;
  }
  return false;
}

function findFigureAnchorForBrightcoveEmbed(embed: Element): Element | null {
  const candidateId =
    (embed as HTMLElement).id ||
    embed.getAttribute("id") ||
    (embed.querySelector("video[id$='_html5_api']") as HTMLElement | null)?.id?.replace(/_html5_api$/i, "") ||
    "";

  // Common podcast/article pattern: player wrapper + figure live inside the same <p>.
  const paragraph = embed.closest("p");
  if (paragraph) {
    const figures = Array.from(paragraph.querySelectorAll("figure"));
    if (candidateId) {
      const matching = figures.find(figure => (figure.getAttribute("data-video-id") || "").trim() === candidateId);
      if (matching) {
        return matching;
      }
    }
    if (figures.length) {
      return figures[figures.length - 1];
    }
  }

  const wrapper = embed.closest(".news-podcast-player-wrapper, .podcast-player-wrapper");
  if (wrapper) {
    const next = wrapper.nextElementSibling;
    if (next && next.tagName.toLowerCase() === "figure") {
      return next;
    }
    const wrapperParentNext = wrapper.parentElement?.nextElementSibling;
    if (wrapperParentNext && wrapperParentNext.tagName.toLowerCase() === "figure") {
      return wrapperParentNext;
    }
  }

  return null;
}

function findOuterBrightcoveAnchor(embed: Element): Element | null {
  // Brightcove playlist/multiplayer embeds often wrap the player in an outer container
  // (e.g. `player_type="brightcove"` / `vjs-playlist-player-container`). Insert the
  // disclaimer after that wrapper so it renders beneath the whole player block.
  const multiplayerWrapper = embed.closest(
    "[player_type='brightcove'], .vjs-playlist-player-container, [class*='video-player-']"
  );
  if (multiplayerWrapper) {
    return multiplayerWrapper;
  }

  const wrapper = embed.closest(
    ".news-podcast-player-wrapper, .podcast-player-wrapper, .brightcove-player-wrapper, .video-player-wrapper"
  );
  if (wrapper) {
    return wrapper;
  }

  const figure = embed.closest("figure");
  if (figure) {
    return figure;
  }

  const paragraph = embed.closest("p");
  if (paragraph) {
    return paragraph;
  }

  return null;
}

function findBrightcoveAnchor(embeds: Element[]): Element | null {
  // Prefer an outer wrapper/figure so disclaimer appears below the player container,
  // not inside nested Brightcove/Video.js internals.
  const videoJs = embeds.find(e => e.tagName.toLowerCase() === "video-js");
  if (videoJs) {
    const outerAnchor = findOuterBrightcoveAnchor(videoJs);
    if (outerAnchor) {
      return outerAnchor;
    }
    const figureAnchor = findFigureAnchorForBrightcoveEmbed(videoJs);
    if (figureAnchor) {
      return figureAnchor;
    }
    return videoJs;
  }

  // If only <video> is present, attach directly after the video element.
  const video = embeds.find(e => e.tagName.toLowerCase() === "video");
  if (video) {
    const outerAnchor = findOuterBrightcoveAnchor(video);
    if (outerAnchor) {
      return outerAnchor;
    }
    const figureAnchor = findFigureAnchorForBrightcoveEmbed(video);
    if (figureAnchor) {
      return figureAnchor;
    }
    return video;
  }

  // Otherwise use the first embed we found.
  return embeds[0] || null;
}

function getBrightcovePlayerIdFromEmbed(embed: Element): string | null {
  const tag = embed.tagName.toLowerCase();

  if (tag === "video-js") {
    const id = (embed as HTMLElement).id || embed.getAttribute("id") || "";
    if (id) {
      return id;
    }
  }

  const directId = (embed as HTMLElement).id || embed.getAttribute("id") || "";
  if (directId && /_html5_api$/i.test(directId)) {
    return directId.replace(/_html5_api$/i, "");
  }

  const nestedVideo = embed.querySelector("video[id$='_html5_api']") as HTMLElement | null;
  if (nestedVideo?.id) {
    return nestedVideo.id.replace(/_html5_api$/i, "");
  }

  const closestVideoJs = embed.closest("video-js") as HTMLElement | null;
  if (closestVideoJs?.id) {
    return closestVideoJs.id;
  }

  return null;
}

function getVideoJsRootForBrightcoveEmbed(embed: Element): HTMLElement | null {
  if (embed.tagName.toLowerCase() === "video-js") {
    return embed as HTMLElement;
  }

  const closest = embed.closest("video-js");
  if (closest && closest.tagName.toLowerCase() === "video-js") {
    return closest as HTMLElement;
  }

  const playerId = getBrightcovePlayerIdFromEmbed(embed);
  if (playerId) {
    const byId = document.getElementById(playerId);
    if (byId && byId.tagName.toLowerCase() === "video-js") {
      return byId as HTMLElement;
    }
  }

  return null;
}

/**
 * Positive signal only. Video.js renders this control even when no captions exist, and
 * keeps it `vjs-hidden` until tracks are attached. A hidden or missing button does not
 * mean the video has no CC. Do not use `.vjs-subs-caps-button` alone: both the outer
 * div and inner button use that class.
 */
function videoJsPlayerHasVisibleCaptionsControl(playerRoot: Element): boolean {
  const bar = playerRoot.querySelector(".vjs-control-bar");
  if (!bar) {
    return false;
  }
  const subsWrapper = bar.querySelector("div.vjs-subs-caps-button.vjs-control");
  if (!subsWrapper) {
    return false;
  }
  return !subsWrapper.classList.contains("vjs-hidden");
}

function isCaptionOrSubtitleKind(kind: string): boolean {
  const k = kind.toLowerCase();
  return k === "captions" || k === "subtitles";
}

function textTrackListHasCaptionOrSubtitleTracks(tracks: TextTrackList | null | undefined): boolean {
  if (!tracks || typeof tracks.length !== "number") {
    return false;
  }
  for (let i = 0; i < tracks.length; i += 1) {
    const t = tracks[i];
    const kind = String(t?.kind || "");
    if (isCaptionOrSubtitleKind(kind)) {
      return true;
    }
  }
  return false;
}

async function waitForVideoJsLoaded(maxWaitMs: number): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-unsafe-any
  const w = window as any;
  if (w.videojs && typeof w.videojs.getPlayer === "function") {
    return;
  }

  const done = deferred<void>();
  const onLoaded = () => done.resolve();
  window.addEventListener("VideoJsLoadedEvent", onLoaded as EventListener, { once: true });

  try {
    const pollIntervalMs = 50;
    const started = Date.now();
    while (Date.now() - started < maxWaitMs) {
      if (w.videojs && typeof w.videojs.getPlayer === "function") {
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await Promise.race([done.promise, delay(pollIntervalMs)]);
      if (w.videojs && typeof w.videojs.getPlayer === "function") {
        return;
      }
    }
  } finally {
    window.removeEventListener("VideoJsLoadedEvent", onLoaded as EventListener);
  }
}

/**
 * HTML5 Brightcove players publish caption tracks on `player.mediainfo` at
 * `loadedmetadata`. Until then `textTracks()` is empty and the CC button stays
 * `vjs-hidden`, which is identical to a video that has no captions. Do not show
 * the disclaimer or fire the View event until catalog metadata is in.
 */
const BRIGHTCOVE_INITIAL_EMBED_WAIT_MS = 4000;
const BRIGHTCOVE_INITIAL_EMBED_POLL_MS = 200;
const BRIGHTCOVE_PLAYER_LOOKUP_MAX_MS = 20000;
const BRIGHTCOVE_METADATA_WAIT_MS = 60000;
const BRIGHTCOVE_POST_METADATA_SETTLE_MS = 3000;
const BRIGHTCOVE_POLL_MS = 200;

type CaptionDecision = "has-captions" | "no-captions" | "unknown";

interface BrightcoveTextTrackInfo {
  kind?: string;
}

interface BrightcoveMediaInfo {
  id?: string;
  textTracks?: BrightcoveTextTrackInfo[];
  text_tracks?: BrightcoveTextTrackInfo[];
}

interface BrightcovePlayer {
  mediainfo?: BrightcoveMediaInfo | null;
  ready?: (callback: () => void) => void;
  one?: (event: string, callback: () => void) => void;
  off?: (event: string, callback: () => void) => void;
  textTracks?: () => TextTrackList;
  remoteTextTracks?: () => TextTrackList;
}

async function waitForInitialBrightcoveEmbeds(maxWaitMs: number, pollIntervalMs: number): Promise<Element[]> {
  const started = Date.now();
  while (Date.now() - started < maxWaitMs) {
    const embeds = findBrightcoveEmbeds();
    if (embeds.length) {
      return embeds;
    }
    // eslint-disable-next-line no-await-in-loop
    await delay(pollIntervalMs);
  }
  return findBrightcoveEmbeds();
}

function tryGetVideoJsPlayer(playerId: string): BrightcovePlayer | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-unsafe-any
  const videojsAny = (window as any).videojs as { getPlayer?: (id: string) => BrightcovePlayer } | undefined;
  if (!videojsAny || typeof videojsAny.getPlayer !== "function") {
    return null;
  }
  try {
    return videojsAny.getPlayer(playerId) || null;
  } catch {
    return null;
  }
}

function mediainfoHasCaptionTracks(player: BrightcovePlayer): boolean {
  const info = player.mediainfo;
  if (!info) {
    return false;
  }
  const tracks = info.textTracks || info.text_tracks;
  if (!Array.isArray(tracks)) {
    return false;
  }
  return tracks.some(track => isCaptionOrSubtitleKind(String(track?.kind || "")));
}

function readPlayerTextTracks(player: BrightcovePlayer, which: "textTracks" | "remoteTextTracks"): boolean {
  const getter = player[which];
  if (typeof getter !== "function") {
    return false;
  }
  try {
    return textTrackListHasCaptionOrSubtitleTracks(getter.call(player));
  } catch {
    return false;
  }
}

function playerHasCaptionEvidence(player: BrightcovePlayer, root: HTMLElement | null): boolean {
  if (mediainfoHasCaptionTracks(player)) {
    return true;
  }
  if (readPlayerTextTracks(player, "textTracks")) {
    return true;
  }
  if (readPlayerTextTracks(player, "remoteTextTracks")) {
    return true;
  }
  return Boolean(root && videoJsPlayerHasVisibleCaptionsControl(root));
}

/** Content catalog is available. `readyState` alone is not enough: a preroll can set it before caption metadata exists. */
function playerCatalogReady(player: BrightcovePlayer): boolean {
  return Boolean(player.mediainfo && player.mediainfo.id);
}

function brightcoveEmbedHasCaptionsNow(embed: Element): boolean {
  if (brightcoveEmbedHasCaptionTracksInMarkup(embed)) {
    return true;
  }
  const playerId = getBrightcovePlayerIdFromEmbed(embed);
  if (!playerId) {
    return false;
  }
  const player = tryGetVideoJsPlayer(playerId);
  if (!player) {
    return false;
  }
  return playerHasCaptionEvidence(player, getVideoJsRootForBrightcoveEmbed(embed));
}

async function lookupVideoJsPlayer(playerId: string, maxWaitMs: number): Promise<BrightcovePlayer | null> {
  const started = Date.now();
  while (Date.now() - started < maxWaitMs) {
    const player = tryGetVideoJsPlayer(playerId);
    if (player) {
      return player;
    }
    // eslint-disable-next-line no-await-in-loop
    await delay(BRIGHTCOVE_POLL_MS);
  }
  return null;
}

function subscribeCaptionTrackAdd(player: BrightcovePlayer, onCaption: () => void): () => void {
  if (typeof player.textTracks !== "function") {
    return () => undefined;
  }
  let tracks: TextTrackList | null = null;
  try {
    tracks = player.textTracks();
  } catch {
    return () => undefined;
  }
  if (!tracks || typeof tracks.addEventListener !== "function") {
    return () => undefined;
  }
  const handler = (event: TrackEvent) => {
    if (isCaptionOrSubtitleKind(String(event.track?.kind || ""))) {
      onCaption();
    }
  };
  tracks.addEventListener("addtrack", handler);
  return () => {
    try {
      tracks?.removeEventListener("addtrack", handler);
    } catch {
      // ignore
    }
  };
}

async function waitForPlayerCatalog(
  player: BrightcovePlayer,
  maxWaitMs: number,
  shouldStop: () => boolean
): Promise<boolean> {
  if (shouldStop() || playerCatalogReady(player)) {
    return playerCatalogReady(player);
  }

  await new Promise<void>(resolve => {
    let settled = false;
    let timer = 0;
    const onMetadata = () => {
      if (shouldStop() || playerCatalogReady(player)) {
        finish();
      }
    };
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearInterval(timer);
      try {
        player.off?.("loadedmetadata", onMetadata);
      } catch {
        // ignore
      }
      resolve();
    };
    const started = Date.now();
    timer = window.setInterval(() => {
      if (shouldStop() || playerCatalogReady(player) || Date.now() - started >= maxWaitMs) {
        finish();
      }
    }, BRIGHTCOVE_POLL_MS);
    try {
      player.one?.("loadedmetadata", onMetadata);
    } catch {
      // Polling still covers catalog readiness.
    }
  });

  return playerCatalogReady(player);
}

/**
 * `unknown` means the HTML5 player never reported catalog metadata. That is still
 * loading (or not inspectable), not proof that captions are missing.
 */
async function decideBrightcoveEmbedCaptions(embed: Element): Promise<CaptionDecision> {
  if (brightcoveEmbedHasCaptionTracksInMarkup(embed)) {
    return "has-captions";
  }

  const playerId = getBrightcovePlayerIdFromEmbed(embed);
  if (!playerId) {
    return "unknown";
  }

  await waitForVideoJsLoaded(BRIGHTCOVE_PLAYER_LOOKUP_MAX_MS);
  const player = await lookupVideoJsPlayer(playerId, BRIGHTCOVE_PLAYER_LOOKUP_MAX_MS);
  if (!player) {
    return "unknown";
  }

  await Promise.race([
    new Promise<void>(resolve => {
      if (typeof player.ready === "function") {
        player.ready(() => resolve());
      } else {
        resolve();
      }
    }),
    delay(BRIGHTCOVE_PLAYER_LOOKUP_MAX_MS)
  ]);

  const root = getVideoJsRootForBrightcoveEmbed(embed);
  if (playerHasCaptionEvidence(player, root)) {
    return "has-captions";
  }

  let captionAdded = false;
  const unsubscribe = subscribeCaptionTrackAdd(player, () => {
    captionAdded = true;
  });
  try {
    const catalogReady = await waitForPlayerCatalog(
      player,
      BRIGHTCOVE_METADATA_WAIT_MS,
      () => captionAdded || playerHasCaptionEvidence(player, root)
    );
    if (captionAdded || playerHasCaptionEvidence(player, root)) {
      return "has-captions";
    }
    if (!catalogReady) {
      return "unknown";
    }

    const settleStarted = Date.now();
    while (Date.now() - settleStarted < BRIGHTCOVE_POST_METADATA_SETTLE_MS) {
      if (captionAdded || playerHasCaptionEvidence(player, root)) {
        return "has-captions";
      }
      // eslint-disable-next-line no-await-in-loop
      await delay(BRIGHTCOVE_POLL_MS);
    }

    if (captionAdded || playerHasCaptionEvidence(player, root)) {
      return "has-captions";
    }
    return "no-captions";
  } finally {
    unsubscribe();
  }
}

async function applyConfirmedBrightcoveDisclaimers(message: DisclaimerMessage): Promise<void> {
  let embeds = findBrightcoveEmbeds();
  if (!embeds.length) {
    embeds = await waitForInitialBrightcoveEmbeds(
      BRIGHTCOVE_INITIAL_EMBED_WAIT_MS,
      BRIGHTCOVE_INITIAL_EMBED_POLL_MS
    );
  }
  if (!embeds.length || hasAnyTranscriptElement()) {
    removeStaleBrightcoveDisclaimers(new Set<string>());
    return;
  }

  const results = await Promise.all(embeds.map(async embed => {
    try {
      return { embed, decision: await decideBrightcoveEmbedCaptions(embed) };
    } catch {
      return { embed, decision: "unknown" as CaptionDecision };
    }
  }));

  if (hasAnyTranscriptElement()) {
    removeStaleBrightcoveDisclaimers(new Set<string>());
    return;
  }

  const activeKeys = new Set<string>();
  results.forEach(({ embed, decision }, index) => {
    if (!embed.isConnected) {
      return;
    }
    const key = getBrightcoveDisclaimerKey(embed, index);
    activeKeys.add(key);
    if (decision !== "no-captions" || brightcoveEmbedHasCaptionsNow(embed)) {
      removeDisclaimer("brightcove", key);
      return;
    }
    const anchor = findBrightcoveAnchor([embed]) || embed;
    const el = ensureDisclaimer("brightcove", message, anchor, key);
    trackDisclaimerViewOnce(el, "brightcove");
  });
  removeStaleBrightcoveDisclaimers(activeKeys);
}

export function checkBrightcoveCaptionsDisclaimer(options?: { message?: DisclaimerMessage }): boolean {
  const message: DisclaimerMessage =
    options?.message ?? DEFAULT_BRIGHTCOVE_DISCLAIMER_BEFORE_CONTACT;

  if (hasAnyTranscriptElement()) {
    removeStaleBrightcoveDisclaimers(new Set<string>());
    return false;
  }

  void applyConfirmedBrightcoveDisclaimers(message).catch(() => {
    // avoid surfacing errors to callers; omit disclaimer on failure
  });
  return findBrightcoveEmbeds().length > 0;
}

export function checkLibsynTranscriptDisclaimer(options?: { message?: DisclaimerMessage }): boolean {
  const message: DisclaimerMessage =
    options?.message ?? DEFAULT_LIBSYN_DISCLAIMER_BEFORE_CONTACT;

  if (!pageHasLibsynEmbed()) {
    return false;
  }

  if (hasAnyTranscriptElement()) {
    return false;
  }

  const el = ensureDisclaimer("libsyn", message, findLibsynAnchor());
  // Libsyn has no late-removal race; track once the disclaimer is in the DOM.
  trackDisclaimerViewOnce(el, "libsyn");
  return true;
}

export function initMediaAccessibilityDisclaimers(): void {
  // Expose globals for Razor / legacy callers.
  (window as any).checkBrightcoveCaptionsDisclaimer = checkBrightcoveCaptionsDisclaimer;
  (window as any).checkLibsynTranscriptDisclaimer = checkLibsynTranscriptDisclaimer;
  (window as any).initMediaAccessibilityDisclaimers = initMediaAccessibilityDisclaimers;

  // Also run automatically on page load.
  checkBrightcoveCaptionsDisclaimer();
  checkLibsynTranscriptDisclaimer();
}

